import { useEffect, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { Checkbox, Icon, Popup, Segment } from "semantic-ui-react";
import FeatureSelectorModal from "../../../../../mapping/components/FeatureSelectorModal";
import { StaticMap } from "../../../../../mapping/components/StaticMap";
import classNames from "classnames";

export function ControlledGeoTargetToggle({ control, name, label }: { control: Control; name: string; label: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ value, onChange }) => {
        const [showInput, setShowInput] = useState(!!value);

        useEffect(() => {
          if (value) {
            setShowInput(true);
          }
        }, [value, setShowInput]);

        return (
          <div className='tw-space-y-2'>
            <div>
              <Checkbox
                label={label}
                checked={showInput}
                onChange={() => {
                  if (showInput) {
                    onChange(null);
                  }
                  setShowInput((prev) => !prev);
                }}
                toggle
                className='tw-flex tw-items-center'
              />
              <div className='-tw-mt-1 tw-ml-[63px] tw-text-sm tw-text-text-hint'>
                Send to addresses within a map boundary
              </div>
            </div>

            {showInput && <GeoTarget value={value} onChange={onChange} readonly={false} />}
          </div>
        );
      }}
    />
  );
}

export function ControlledGeoTarget({
  control,
  name,
  label,
  readonly,
  className,
}: {
  control: Control;
  name: string;
  label?: string;
  readonly?: boolean;
  className?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ value, onChange }) => (
        <GeoTarget value={value} onChange={onChange} label={label} readonly={readonly} className={className} />
      )}
    />
  );
}

export function GeoTarget({
  value,
  onChange = () => {},
  label,
  readonly,
  className,
}: {
  value: { featureIds: number[]; restrictBoundary: boolean } | null;
  onChange?: (value: { featureIds: number[]; restrictBoundary: boolean } | null) => void;
  label?: string;
  readonly?: boolean;
  className?: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const featureIds = value?.featureIds || [];
  const restrictBoundary = value?.restrictBoundary || false;

  const hasFeatures = featureIds.length > 0;

  if (readonly) {
    return (
      <>
        {label && (
          <label className='tw-font-bold'>
            {label} {restrictBoundary ? "(restricted to)" : "(included)"}
          </label>
        )}
        <div className='tw-flex tw-flex-col'>
          {hasFeatures ? (
            <Segment className={classNames("tw-max-h-[17rem] tw-p-0", className)}>
              <StaticMap featureIds={featureIds} expandExtent={1.5} />
            </Segment>
          ) : (
            <p>No boundaries selected</p>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {label && (
        <label>
          {label} {restrictBoundary ? "(restricted to)" : "(included)"}
        </label>
      )}
      {isModalOpen && (
        <FeatureSelectorModal
          onSaveSelection={(featureIds) => {
            onChange({
              featureIds,
              restrictBoundary,
            });
            setIsModalOpen(false);
          }}
          initialSelectedFeatures={featureIds || []}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      {hasFeatures ? (
        <>
          <div
            role='button'
            tabIndex={0}
            className='tw-group tw-relative tw-cursor-pointer tw-overflow-hidden focus-visible:tw-ring-1 '
            onClick={() => setIsModalOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsModalOpen(true);
              }
            }}
          >
            <Segment className='tw-m-0 tw-h-[17rem] tw-p-0'>
              <StaticMap featureIds={featureIds} expandExtent={1.5} />
            </Segment>
            <div
              className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-black/40 tw-font-bold tw-text-white tw-opacity-0 tw-transition-opacity tw-duration-100 group-hover:tw-opacity-100'
              tabIndex={-1}
            >
              Change Boundaries
            </div>
          </div>
        </>
      ) : (
        <Segment
          placeholder
          tertiary
          className='tw-h-[10.5rem] tw-min-h-0 tw-cursor-pointer tw-rounded-lg tw-border-[2px] tw-border-dashed tw-border-black-light tw-py-6 tw-text-center'
          onClick={() => setIsModalOpen(true)}
        >
          <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-black'>
            <Icon name='plus' size='big' />
            <p className='tw-mt-1 tw-text-lg'>Add Boundaries</p>
          </div>
        </Segment>
      )}

      {hasFeatures && (
        <div className='tw-mt-2'>
          <Checkbox
            toggle
            label='Restrict to selected boundaries'
            defaultChecked={restrictBoundary}
            onChange={(e, { checked }) => {
              onChange({
                featureIds,
                restrictBoundary: !!checked,
              });
            }}
          />
          <Popup
            content='By default, geo-targeting will include additional users in the notification on top of those selected by Subscription Groups. Enable this option to EXCLUDE users outside the boundary instead.'
            trigger={<Icon name='question circle outline' className='tw-ml-1' />}
            wide
            size='small'
            position='bottom center'
          />
        </div>
      )}
    </>
  );
}
